import { fetchData } from '../configuration/setup/fetch';
import type { BoundingBox } from '../features/map/mapSlice';
import type { CoordinatesShort } from './types';

export const MIN_TRAFFIC_INCIDENTS_ZOOM = 11;

export type TrafficIncidentType =
    | 'accident'
    | 'construction'
    | 'congestion'
    | 'roadHazard'
    | 'roadClosure'
    | 'disabledVehicle'
    | 'massTransit'
    | 'plannedEvent'
    | 'weather'
    | 'laneRestriction'
    | 'other';

export type TrafficIncident = {
    id: string;
    criticality: 'low' | 'minor' | 'major' | 'critical';
    description: {
        value: string;
        language?: string;
    };
    summary: {
        value: string;
        language?: string;
    };
    comment?: string;
    type: TrafficIncidentType;
    startTime?: string;
    endTime?: string;
    roadClosed: boolean;
    position: CoordinatesShort;
};

type HereTrafficIncident = {
    incidentDetails?: {
        id?: string;
        criticality?: TrafficIncident['criticality'];
        description?: TrafficIncident['description'];
        summary?: TrafficIncident['summary'];
        comment?: string;
        type?: TrafficIncidentType;
        startTime?: string;
        endTime?: string;
        roadClosed?: boolean;
    };
    location?: {
        shape?: {
            links?: {
                points?: CoordinatesShort[];
            }[];
        };
    };
};

type HereTrafficIncidentsResponse = {
    results?: HereTrafficIncident[];
};

const buildTrafficIncidentsSearchArea = (boundingBox: BoundingBox) => {
    const bbox = [
        boundingBox.topLeft.lng,
        boundingBox.bottomRight.lat,
        boundingBox.bottomRight.lng,
        boundingBox.topLeft.lat,
    ].join(',');

    return `bbox:${bbox}`;
};

export const buildTrafficIncidentsUrl = (boundingBox: BoundingBox, apiKey: string, language?: string) =>
    [
        'https://data.traffic.hereapi.com/v7/incidents?in=',
        buildTrafficIncidentsSearchArea(boundingBox),
        '&locationReferencing=shape',
        language ? `&lang=${encodeURIComponent(language)}` : '',
        `&apiKey=${apiKey}`,
    ].join('');

const getFirstIncidentPoint = (incident: HereTrafficIncident): CoordinatesShort | undefined =>
    incident.location?.shape?.links?.[0]?.points?.[0];

const mapTrafficIncident = (incident: HereTrafficIncident): TrafficIncident | undefined => {
    const incidentDetails = incident.incidentDetails;
    const position = getFirstIncidentPoint(incident);

    if (!incidentDetails?.id || !position) {
        return;
    }

    return {
        id: incidentDetails.id,
        criticality: incidentDetails.criticality ?? 'low',
        description: incidentDetails.description ?? { value: '' },
        summary: incidentDetails.summary ?? { value: '' },
        comment: incidentDetails.comment,
        type: incidentDetails.type ?? 'other',
        startTime: incidentDetails.startTime,
        endTime: incidentDetails.endTime,
        roadClosed: incidentDetails.roadClosed ?? false,
        position,
    };
};

export const mapTrafficIncidents = (response: HereTrafficIncidentsResponse): TrafficIncident[] =>
    response.results?.map(mapTrafficIncident).filter((incident): incident is TrafficIncident => !!incident) ?? [];

export const isTrafficRoadRestriction = (incident: TrafficIncident) =>
    incident.roadClosed || incident.type === 'roadClosure' || incident.type === 'laneRestriction';

export const fetchTrafficIncidents = async (
    boundingBox: BoundingBox,
    apiKey: string,
    language?: string
): Promise<TrafficIncident[]> => {
    const response = await fetchData(buildTrafficIncidentsUrl(boundingBox, apiKey, language));

    return mapTrafficIncidents(response);
};
